import { Platform } from 'react-native';
import Constants from 'expo-constants';
import Axios from 'axios';
import * as ExpoFileLogger from 'expo-file-logger';
import AppConstants from './Constants';
import { AppActions, AppStatus } from '../contexts/App';
import { addToCache, getFromCache } from './Cache';

const axios = Axios.create({ baseURL: Constants.expoConfig.extra?.baseUrl });
const performance = { now: () => Date.now() }; // @tim.cook I have no idea where performance came from nor why it's undeclared @jacksonkr

if (Platform.OS.equals('web') && (__DEV__ || Constants.expoConfig.extra?.enablePerformanceLogging === 'true')) {
    axios.interceptors.request.use(config => {
        config.requestStartTime = performance.now();
        return config;
    });

    axios.interceptors.response.use(response => {
        const cacheHit = response.data.cacheHit || false;
        const duration = (performance.now() - response.config.requestStartTime) / 1000;
        console.log(`Request [${response.config.url}] took ${duration}s, cacheHit=${cacheHit}, serverTime=${response.data?.timeSpan}`);
        return response;
    });
}

const axiosForApi = Axios.create({ baseURL: Constants.expoConfig.extra.apiUrl, timeout: 10000 });

if (Platform.OS.equals('web') && (__DEV__ || Constants.expoConfig.extra?.enablePerformanceLogging === 'true')) {
    axiosForApi.interceptors.request.use(config => {
        config.requestStartTime = performance.now();
        return config;
    });

    axiosForApi.interceptors.response.use(response => {
        const duration = (performance.now() - response.config.requestStartTime) / 1000;
        console.log(`Request [${response.config.url}] took ${duration}s`);
        return response;
    });
}

export const fetchDomainItemByKey = async (domain, key) => {
    if (!domain || !key) {
        ExpoFileLogger.log('warning', 'Cannot fetch a domain item without doman or item key');
        return null;
    }

    ExpoFileLogger.log('debug', `Fetching domain[${domain}] item by key[${key}]`);

    const endpoint = `${AppConstants.REACT_APP_DOMAIN_ITEM_BY_KEY_BASE}?domain=${domain}&key=${key}`;
    const cached = getFromCache(endpoint);

    if (cached) {
        return cached;
    }

    try {
        const response = await axios.get(endpoint);

        if (response) {
            addToCache(endpoint, response.data);

            return response.data;
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error fetching domain item with key ${key} - ${e.message}`);
    }

    return null;
};

export const fetchFeaturedService = async (campus, speaker) => {
    let endpoint = AppConstants.REACT_APP_FEATURED_SERVICE_BASE;

    if (campus) {
        endpoint += `?campus=${campus}`;
    }

    if (speaker) {
        if (endpoint.indexOf('campus') !== -1) {
            endpoint += `&speaker=${speaker}`;
        } else {
            endpoint += `?speaker=${speaker}`;
        }
    }

    const cached = getFromCache(endpoint);

    if (cached) {
        return cached;
    }

    ExpoFileLogger.log('debug', `Fetching featured service from [${endpoint}]`);

    try {
        const response = await axios.get(endpoint);

        if (response) {
            addToCache(endpoint, response.data);

            return response.data;
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error fetching featured service - ${e.message}`);
    }

    return null;
};

export const fetchPage = async (dispatch, path) => {
    const endpoint = AppConstants.REACT_APP_PAGE_BASE + path;

    ExpoFileLogger.log('debug', `Fetching page [${endpoint}]`);

    let cachedDocument = getFromCache(endpoint);

    if (cachedDocument) {
        console.log(`Serving page from cache [${path}]`);
        ExpoFileLogger.log('debug', `Serving page from cache [${path}]`);

        dispatch({
            data: cachedDocument,
            href: path,
            status: AppStatus.READY,
            type: AppActions.API_REQUEST
        });
    } else {
        dispatch({
            endpoint,
            type: AppActions.API_STATE_CHANGE,
            status: AppStatus.LOADING
        });

        try {
            const response = await axios.get(endpoint);

            if (response.data) {
                ExpoFileLogger.log('debug', `Successfully fetched page [${path}]`);

                dispatch({
                    data: response.data,
                    href: path,
                    status: AppStatus.READY,
                    type: AppActions.API_REQUEST
                });

                addToCache(endpoint, response.data);

                return response.data;
            }
        } catch (e) {
            ExpoFileLogger.log('error', `API encountered and error @ endpoint [${endpoint}] with error - [${e.message}]`);

            dispatch({
                status: AppStatus.FAILED,
                type: AppActions.API_REQUEST
            });

            return e;
        }
    }

    return null;
};

export const fetchStartupData = async startPage => {
    const cached = getFromCache('startup_config');
    const startupData = {};

    if (cached) {
        return cached;
    } else {
        try {
            const settings = await axios.get(AppConstants.REACT_APP_SETTINGS_BASE);
            let subscriptions;

            try {
                subscriptions = await axiosForApi.get(AppConstants.REACT_APP_SUBSCRIPTION_INFO + settings?.data?.websiteSettings?.domain);

                if (subscriptions.data) {
                    settings.data.subscriptions = subscriptions.data;
                }
            } catch (ex) {
                ExpoFileLogger.log('Error', ex.message);
            }

            startupData.settings = settings.data;

            try {
                const countries = await axiosForApi.get(AppConstants.REACT_APP_GET_COUNTRIES_BASE);

                if (countries.data) {
                    startupData.countries = countries.data;
                }
            } catch (ex) {
                ExpoFileLogger.log('Error', ex.message);
            }

            const startupDataRequest = await axios.get(AppConstants.REACT_APP_STARTUP_DATA_BASE);

            if (startupDataRequest.data) {
                const siteWrapperWeb = startupDataRequest.data?.siteWrapperWeb;
                const siteWrapperApp = startupDataRequest.data?.siteWrapperApp;

                if (siteWrapperWeb || siteWrapperApp) {
                    startupData.siteWrapper = {
                        web: siteWrapperWeb,
                        native: { ...siteWrapperWeb, ...siteWrapperApp }
                    };
                }

                const domainRedirects = startupDataRequest.data?.redirects;

                if (domainRedirects) {
                    const redirects = {};

                    Object.values(domainRedirects.contentItems ?? {}).forEach(redirect => {
                        redirects[redirect.legacyUrl] = redirect;
                    });

                    startupData.redirects = redirects;
                }

                const domainVanities = startupDataRequest.data?.vanityUrls;

                if (domainVanities) {
                    const vanities = {};

                    Object.values(domainVanities.contentItems ?? {}).forEach(async vanity => {
                        const isExternal = vanity.redirectTo.startsWith('http');

                        vanities[vanity.nodeName.toLowerCase()] = {
                            internal: !isExternal,
                            url: vanity.redirectTo
                        };
                    });

                    startupData.vanityUrls = vanities;
                }
            }
        } catch (e) {
            console.log(`Error loading global settings: ${e.message}`);
            ExpoFileLogger.log('error', `Error loading global settings: ${e.message}`);
        }

        try {
            const navigationResponse = await axios.get(AppConstants.REACT_APP_NAVIGATION_BASE);

            if (navigationResponse.data) {
                startupData.navigation = navigationResponse.data.navigation;
            }
        } catch (e) {
            ExpoFileLogger.log('error', `Error loading navigation: [${e.message}]`);
        }

        try {
            if (Platform.OS.equals('web')) {
                if (startupData?.redirects?.hasOwnProperty(window.location.pathname)) {
                    startPage = startupData.redirects[window.location.pathname].redirectTo;
                }

                let vanityUrl = startupData.vanityUrls?.[window.location.pathname.slice(1).toLowerCase()];

                if (vanityUrl?.internal) {
                    startPage = vanityUrl.url;
                }
            }

            const endpoint = `${AppConstants.REACT_APP_PAGE_BASE}${startPage}&domain=${startupData.settings.websiteSettings.domain}`;

            ExpoFileLogger.log('debug', `Fetching startup page[${endpoint}]`);

            const response = await axios.get(endpoint);

            if (response?.data) {
                const key = endpoint.replaceAll(Constants.expoConfig.extra.baseUrl, '');
                addToCache([key, key.replace(/&.*/i, '')], response.data);
                startupData.activePage = response.data;
            }
        } catch (e) {
            ExpoFileLogger.log('error', `Error pre-loading start page: [${e.message}]`);
        }

        addToCache('startup_config', startupData);
    }

    return startupData;
};

export const fetchVcmsContent = async haivisionPublicId => {
    const endpoint = `${AppConstants.REACT_APP_VCMS_MEDIA_BASE}${haivisionPublicId}`;

    const cached = getFromCache(endpoint);

    if (cached) {
        return cached;
    }

    ExpoFileLogger.log('debug', `Fetching VCMS content for PID[${haivisionPublicId}]`);

    try {
        const response = await axios.get(endpoint);

        if (response) {
            ExpoFileLogger.log('debug', `Successfully fetched VCMS content for PID[${haivisionPublicId}]`);

            addToCache(endpoint, response.data);

            return response.data;
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error fetching VCMS content for PID[${haivisionPublicId}] - [${e.message}]`);
    }

    return null;
};

export const post = async (endpoint, body, onSuccess, contentType = 'multipart/form-data', extraHeaders) => {
    ExpoFileLogger.log('debug', `Posting to [${endpoint}]`);

    let data = body;

    if (body && contentType?.equals('multipart/form-data')) {
        data = new FormData();
        Object.keys(body).forEach(key => data.append(key, body[key]));
    }

    try {
        const response = await axios.post(endpoint, data, {
            headers: {
                'Content-Type': contentType,
                ...extraHeaders
            }
        });

        if (response) {
            ExpoFileLogger.log('debug', `Successfully posted to [${endpoint}]`);

            if (onSuccess) {
                onSuccess(response.data);
            }
            return response.data;
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error posting to [${endpoint}] - [${e.message}]`);
    }

    return null;
};

export const postSync = (endpoint, body, contentType = 'multipart/form-data') => {
    ExpoFileLogger.log('debug', `Posting syncrhonously to [${endpoint}]`);

    let data = body;

    if (contentType.equals('multipart/form-data')) {
        data = new FormData();
        Object.keys(body).forEach(key => data.append(key, body[key]));
    }

    return axios.post(endpoint, data, {
        headers: {
            'Content-Type': contentType
        }
    });
};

export const get = async endpoint => {
    ExpoFileLogger.log('debug', `Retrieveing from [${endpoint}]`);

    try {
        const response = await axios.get(endpoint);

        if (response) {
            ExpoFileLogger.log('debug', `Successfully retrieved from [${endpoint}]`);

            return response.data;
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error retrieving from [${endpoint}] - [${e.message}]`);
    }

    return null;
};

export const fetch = async (endpoint, body, method = 'GET', headers, contentType = 'application/json') => {
    if (!endpoint) {
        return;
    }

    ExpoFileLogger.log('debug', `Fetching from [${endpoint}]`);

    try {
        const response = await window.fetch(endpoint, {
            method,
            headers: {
                ...headers,
                'Content-Type': contentType
            },
            body: body && JSON.stringify(body)
        });

        if (response?.ok) {
            return await response.json();
        }
    } catch (e) {
        ExpoFileLogger.log('error', `Error fetching from [${endpoint}] - [${e.message}]`);
    }
};
